import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createTheme, deleteTheme, getThemes, updateTheme, ThemePayload } from '@/api/themesService';
import { queryKeys } from '@/lib/queryKeys';
import { toaster } from '@/components/ui/toaster';

const errorMessage = (error: unknown) => {
  const res = (error as { response?: { data?: { message?: string } } })?.response;
  if (res?.data?.message) return res.data.message;
  return error instanceof Error ? error.message : 'Une erreur est survenue';
};

const PAGE_SIZE = 20;

export const useThemes = (filters: { search?: string; page: number }) => {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'pv2', 'themes'] });
    queryClient.invalidateQueries({ queryKey: queryKeys.pv2ThemesRef });
  };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.pv2Themes(filters),
    queryFn: () => getThemes({ search: filters.search, page: filters.page, pageSize: PAGE_SIZE }),
  });

  const createMutation = useMutation({
    mutationFn: (payload: ThemePayload) => createTheme(payload),
    onSuccess: () => {
      invalidate();
      toaster.create({ title: 'Thématique créée', type: 'success' });
    },
    onError: (error) => toaster.create({ title: 'Erreur', description: errorMessage(error), type: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ThemePayload }) => updateTheme(id, payload),
    onSuccess: () => {
      invalidate();
      toaster.create({ title: 'Thématique mise à jour', type: 'success' });
    },
    onError: (error) => toaster.create({ title: 'Erreur', description: errorMessage(error), type: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTheme(id),
    onSuccess: () => {
      invalidate();
      toaster.create({ title: 'Thématique supprimée', type: 'success' });
    },
    onError: (error) => toaster.create({ title: 'Suppression impossible', description: errorMessage(error), type: 'error' }),
  });

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    pageSize: PAGE_SIZE,
    isLoading,
    createTheme: createMutation.mutateAsync,
    updateTheme: updateMutation.mutateAsync,
    deleteTheme: deleteMutation.mutateAsync,
    isSaving: createMutation.isPending || updateMutation.isPending,
    invalidate,
  };
};
